<template>
  <ba-dialog
    :show="dialog"
    v-on:show-dialog="dialog = true"
    btnColor="success"
    :btnName="$t('_createCategory')"
    :btnTooltip="$t('_createCategory')"
    :title="$t('_createCategory')"
    :onHide="hideDialog"
  >
    <v-container fluid grid-list-xl>
      <v-alert type="error" :value="errors.length > 0" v-for="error in errors" :key="error.message">
        {{ error.message }}
      </v-alert>

      <v-layout wrap align-center>
        <v-flex xs12>
          <v-text-field :label="$t('_remoteId')" v-model="remoteId"></v-text-field>
        </v-flex>

        <v-flex xs12>
          <v-text-field :label="$t('_name')" v-model="name" :rules="[rules.max200]" />
        </v-flex>

        <v-flex xs12>
          <v-text-field :label="$t('_description')" v-model="description" :rules="[rules.max200]" />
        </v-flex>

        <v-flex xs12>
          <v-select
            :items="categoryTypes"
            v-model="categoryType"
            :label="$tc('_categoryType')"
            item-text="name"
            item-value="name"
          ></v-select>
        </v-flex>

        <v-flex xs12 v-if="checkFeatureFlag('create-manual-connection', features)">
          <v-select
            :items="accountingConnections"
            v-model="connectionId"
            label="Accounting Connection"
            item-text="name"
            item-value="id"
          >
            <template v-slot:item="{ item }">
              <span>{{ item.name || item.provider }} ({{ item.id }})</span>
            </template>
          </v-select>
        </v-flex>
      </v-layout>
      <v-flex xs12 sm12 md6>
        <v-btn @click="save" :disabled="!formValid || savingCategory || isLoading > 0" color="primary text-capitalize">
          {{ $t('_save') }}
        </v-btn>
      </v-flex>
    </v-container>
  </ba-dialog>
</template>
<script>
import gql from 'graphql-tag';

import { types } from '../../contracts/categoryTypes';

export default {
  props: {
    refresh: {
      type: Function,
      required: false,
    },
    connections: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      connectionId: 'Manual', // Default to 'Manual' for backwards compatibility
      dialog: false,
      name: '',
      description: '',
      remoteId: '',
      categoryTypes: types,
      categoryType: types[0].name,
      savingCategory: false,
      errors: [],
      rules: {
        max200: (v) => !v || v.length <= 200 || this.$t('_maxTwentyChars'),
      },
    };
  },
  watch: {
    dialog() {
      if (!this.dialog) {
        this.resetForm();
      }
    },
  },
  computed: {
    formValid() {
      return this.name.trim() !== '' && this.remoteId.trim() !== '';
    },
    features() {
      return this.$store.getters.features || {};
    },
    accountingConnections() {
      // Get all manual connections (both legacy 'Manual' and new manual provider connections)
      const manualConnections = (this.connections || []).filter(
        (conn) => conn.provider === 'Manual' && !conn.isDeleted
      );

      // Add legacy Manual option if feature flag is enabled
      const options = [
        { id: 'Manual', name: 'Manual (Legacy)', provider: 'Manual' },
        ...manualConnections.map((conn) => ({
          id: conn.id,
          name: conn.name || `Manual Connection`,
          provider: conn.provider,
        })),
      ];

      return options;
    },
  },
  methods: {
    hideDialog() {
      this.dialog = false;
    },
    resetForm() {
      this.name = '';
      this.description = '';
      this.remoteId = '';
      this.categoryType = types[0].name;
      this.connectionId = 'Manual'; // Reset to default
      this.savingCategory = false;
      this.errors = [];
    },
    async save() {
      try {
        this.savingCategory = true;
        this.errors = []; // Clear previous errors
        const orgId = this.$store.state.currentOrg.id;

        const category = {
          connectionId: this.connectionId,
          remoteId: this.remoteId,
          name: this.name,
          description: this.description,
          type: this.categoryType,
        };

        const resp = await this.$apollo.mutate({
          mutation: gql`
            mutation CreateCategory($orgId: ID!, $category: CreateCategoryInput!) {
              createCategory(orgId: $orgId, category: $category) {
                success
                errors
              }
            }
          `,
          variables: {
            orgId,
            category,
          },
        });

        if (resp.data.createCategory.success) {
          this.resetForm();
          this.hideDialog();
          this.$emit('refresh');
        } else {
          // Show errors but don't reset form so user can fix issues
          resp.data.createCategory.errors.forEach((error) => {
            this.errors.push({ message: error });
          });
        }
      } catch (error) {
        console.error('Error creating category:', error);
        this.errors.push({ message: 'Network error: Failed to create category. Please try again.' });
      } finally {
        this.savingCategory = false;
      }
    },
  },
};
</script>
